import { formatDayLabel } from "../../lib/partnerReports";

export interface DailyBarPoint {
  day: string;
  value: number;
}

interface DailyBarsProps {
  points: DailyBarPoint[];
  ariaLabel: string;
  tone?: "accent" | "info" | "success";
  formatValue?: (value: number) => string;
  heightClass?: string;
}

const TONE_CLASS: Record<NonNullable<DailyBarsProps["tone"]>, string> = {
  accent: "bg-[var(--accent)]",
  info: "bg-[var(--info)]",
  success: "bg-[var(--success)]",
};

/** Dependency-free daily bar chart (orders / income per day). */
export function DailyBars({
  points,
  ariaLabel,
  tone = "accent",
  formatValue,
  heightClass = "h-28",
}: DailyBarsProps) {
  if (points.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-[var(--text-muted)]">—</p>
    );
  }
  const max = Math.max(1, ...points.map((p) => p.value));

  return (
    <div className={`flex items-end gap-1 ${heightClass}`} role="img" aria-label={ariaLabel}>
      {points.map((point) => {
        const ratio = point.value / max;
        const label = formatValue ? formatValue(point.value) : String(point.value);
        return (
          <div
            key={point.day}
            className="flex h-full min-w-[4px] flex-1 flex-col justify-end"
            title={`${formatDayLabel(point.day)} · ${label}`}
          >
            <div className="flex h-full w-full items-end overflow-hidden rounded-t-[4px] bg-[color-mix(in_srgb,var(--text-muted)_8%,transparent)]">
              <div
                className={`w-full rounded-t-[4px] ${TONE_CLASS[tone]} ${
                  point.value === 0 ? "opacity-0" : ""
                }`}
                style={{ height: `${Math.max(3, Math.round(ratio * 100))}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DailyBarsCaption({ first, last }: { first: string; last: string }) {
  return (
    <div className="mt-1 flex items-center justify-between text-[11px] text-[var(--text-muted)]">
      <span>{formatDayLabel(first)}</span>
      <span>{formatDayLabel(last)}</span>
    </div>
  );
}
